import { Box } from "@mui/material";
import pluralize from "pluralize";
import React from "react";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { isExplorePage } from "utils/utilityFunctions";

import { ImageQueryGrid } from "./ImageQueryGrid";
import { PageContext } from "./ImageQueryPreview";

interface ActivityProps {
  detector: Detector | undefined;
  errorExplorePageImageQueries: Error | undefined;
  errorFlaggedImageQueries: Error | undefined;
  errorNoImageQueries: Error | undefined;
  errorYesImageQueries: Error | undefined;
  detectorPageImageQueries: ListResponse<ImageQuery> | undefined;
  imageQueriesFlagged: ListResponse<ImageQuery> | undefined;
  noAnsweredImageQueries: ListResponse<ImageQuery> | undefined;
  onRefetch: () => Promise<void>;
  pageContext: PageContext;
  yesAnsweredImageQueries: ListResponse<ImageQuery> | undefined;
  noTextImageQueries?: ListResponse<ImageQuery> | undefined;
  errorNoTextImageQueries?: Error | undefined;
  hasTextImageQueries?: ListResponse<ImageQuery> | undefined;
  errorHasTextImageQueries?: Error | undefined;
  multichoiceExamples: { [key: string]: ListResponse<ImageQuery> | undefined };
  errorMultichoiceExamples: Error | undefined;
  hasBoxesImageQueries?: ListResponse<ImageQuery> | undefined;
  noBoxesImageQueries?: ListResponse<ImageQuery> | undefined;
  errorHasBoxesImageQueries?: Error | undefined;
  errorNoBoxesImageQueries?: Error | undefined;
  greaterThanMaxImageQueries?: ListResponse<ImageQuery> | undefined;
  errorGreaterThanMaxImageQueries?: Error | undefined;
  countExamples: { [key: string]: ListResponse<ImageQuery> | undefined };
  errorCountExamples: Error | undefined;
  columns?: number;
  isExamples?: boolean;
}

export enum STATUS {
  YES_ANSWERED = "yes",
  NO_ANSWERED = "no",
  FLAGGED = "unclear",
  NO_TEXT = "NO_TEXT",
  HAS_TEXT = "HAS_TEXT",
  BOUNDING_BOX = "BOUNDING_BOX",
  GREATER_THAN_MAX = "GREATER_THAN_MAX",
  NO_OBJECTS = "NO_OBJECTS",
}

export const Activity = React.memo((props: ActivityProps) => {
  const {
    detector,
    errorExplorePageImageQueries,
    errorFlaggedImageQueries,
    errorNoImageQueries,
    errorYesImageQueries,
    detectorPageImageQueries,
    imageQueriesFlagged,
    noAnsweredImageQueries,
    onRefetch,
    pageContext,
    yesAnsweredImageQueries,
    noTextImageQueries,
    hasTextImageQueries,
    errorNoTextImageQueries,
    errorHasTextImageQueries,
    hasBoxesImageQueries,
    noBoxesImageQueries,
    errorHasBoxesImageQueries,
    errorNoBoxesImageQueries,
    greaterThanMaxImageQueries,
    errorGreaterThanMaxImageQueries,
    multichoiceExamples,
    errorMultichoiceExamples,
    countExamples,
    errorCountExamples,
    columns,
    isExamples,
  } = props;

  const getSkeletonIndicator = (loading: boolean, error: Error | undefined) =>
    SkeletonIndicator({
      height: 250,
      className: "my-4",
      loading,
      error,
    });

  const noAnsweredStatus = getSkeletonIndicator(!noAnsweredImageQueries, errorNoImageQueries);
  const yesAnsweredStatus = getSkeletonIndicator(!yesAnsweredImageQueries, errorYesImageQueries);
  const flaggedAnsweredStatus = getSkeletonIndicator(!imageQueriesFlagged, errorFlaggedImageQueries);
  const explorePageStatus = getSkeletonIndicator(!detectorPageImageQueries, errorExplorePageImageQueries);
  const countExamplesStatus = getSkeletonIndicator(Object.keys(countExamples).length === 0, errorCountExamples);
  const choiceExamplesStatus = getSkeletonIndicator(!multichoiceExamples, errorMultichoiceExamples);
  const noTextStatus = getSkeletonIndicator(!noTextImageQueries, errorNoTextImageQueries);
  const hasTextStatus = getSkeletonIndicator(!hasTextImageQueries, errorHasTextImageQueries);
  const hasBoxesStatus = getSkeletonIndicator(!hasBoxesImageQueries, errorHasBoxesImageQueries);
  const noBoxesStatus = getSkeletonIndicator(!noBoxesImageQueries, errorNoBoxesImageQueries);
  const greaterThanMaxStatus = getSkeletonIndicator(!greaterThanMaxImageQueries, errorGreaterThanMaxImageQueries);
  const getMultichoiceExample = () => (
    <>
      {detector?.mode_configuration?.class_names?.map((choice) => (
        <ImageQueryGrid
          key={choice}
          title={choice}
          status={choiceExamplesStatus}
          imageQueries={multichoiceExamples[choice]}
          onRefetch={onRefetch}
          detector={detector}
          pageContext={pageContext}
          columns={columns}
        />
      ))}
      <ImageQueryGrid
        title={STATUS.FLAGGED}
        status={flaggedAnsweredStatus}
        imageQueries={imageQueriesFlagged}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
    </>
  );

  const getDefaultExample = () => (
    <Box className="mt-6">
      <ImageQueryGrid
        title=""
        status={explorePageStatus}
        imageQueries={detectorPageImageQueries}
        onRefetch={onRefetch}
        detector={detector}
        pageContext={pageContext}
        columns={columns}
      />
    </Box>
  );

  const getBinaryExample = () => (
    <>
      <ImageQueryGrid
        title={STATUS.YES_ANSWERED}
        status={yesAnsweredStatus}
        imageQueries={yesAnsweredImageQueries}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
      <ImageQueryGrid
        title={STATUS.NO_ANSWERED}
        status={noAnsweredStatus}
        imageQueries={noAnsweredImageQueries}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
      <ImageQueryGrid
        title={STATUS.FLAGGED}
        status={flaggedAnsweredStatus}
        imageQueries={imageQueriesFlagged}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
    </>
  );

  const getCountExample = () => {
    if (isExamples) {
      return (
        <>
          <ImageQueryGrid
            title="Count"
            status={countExamplesStatus}
            imageQueries={countExamples.COUNT}
            onRefetch={onRefetch}
            detector={detector}
            pageContext={pageContext}
            columns={columns}
          />
          <ImageQueryGrid
            title={STATUS.FLAGGED}
            status={flaggedAnsweredStatus}
            imageQueries={imageQueriesFlagged}
            onRefetch={onRefetch}
            detector={detector}
            columns={columns}
          />
        </>
      );
    }

    const maxCount = detector?.mode_configuration?.max_count || 0;
    const options = Array.from({ length: maxCount + 1 }, (_, i) => i.toString());

    return (
      <>
        {options.map((option) => (
          <ImageQueryGrid
            title={`${option} ${pluralize("Object", parseInt(option, 10))}`}
            answerValue={`${option}`}
            status={countExamplesStatus}
            imageQueries={countExamples[option]}
            onRefetch={onRefetch}
            detector={detector}
            pageContext={pageContext}
            columns={columns}
          />
        ))}
        <ImageQueryGrid
          title={`>${maxCount} ${pluralize("Object", maxCount)}`}
          answerValue={`>${maxCount}`}
          status={countExamplesStatus}
          imageQueries={countExamples.GREATER_THAN_MAX}
          onRefetch={onRefetch}
          detector={detector}
          pageContext={pageContext}
          columns={columns}
        />
        <ImageQueryGrid
          title={STATUS.FLAGGED}
          status={flaggedAnsweredStatus}
          imageQueries={imageQueriesFlagged}
          onRefetch={onRefetch}
          detector={detector}
          columns={columns}
        />
      </>
    );
  };

  const getTextExample = () => (
    <>
      <ImageQueryGrid
        title={STATUS.NO_TEXT}
        status={noTextStatus}
        imageQueries={noTextImageQueries}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
      <ImageQueryGrid
        // the first character of the title is capitalized
        title={STATUS.HAS_TEXT}
        status={hasTextStatus}
        imageQueries={hasTextImageQueries}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
      <ImageQueryGrid
        title={STATUS.FLAGGED}
        status={flaggedAnsweredStatus}
        imageQueries={imageQueriesFlagged}
        onRefetch={onRefetch}
        detector={detector}
        columns={columns}
      />
    </>
  );

  const getBoundingBoxExample = () => {
    const maxBoxes = detector?.mode_configuration?.max_num_bboxes || 0;
    return (
      <>
        <ImageQueryGrid
          title="No objects"
          answerValue={STATUS.NO_OBJECTS}
          status={noBoxesStatus}
          imageQueries={noBoxesImageQueries}
          onRefetch={onRefetch}
          detector={detector}
          columns={columns}
        />
        <ImageQueryGrid
          title="Bounding Boxes"
          answerValue={STATUS.BOUNDING_BOX}
          status={hasBoxesStatus}
          imageQueries={hasBoxesImageQueries}
          onRefetch={onRefetch}
          detector={detector}
          columns={columns}
        />
        <ImageQueryGrid
          title={`>${maxBoxes} ${pluralize("Object", maxBoxes)}`}
          answerValue={STATUS.GREATER_THAN_MAX}
          status={greaterThanMaxStatus}
          imageQueries={greaterThanMaxImageQueries}
          onRefetch={onRefetch}
          detector={detector}
          columns={columns}
        />
        <ImageQueryGrid
          title={STATUS.FLAGGED}
          status={flaggedAnsweredStatus}
          imageQueries={imageQueriesFlagged}
          onRefetch={onRefetch}
          detector={detector}
          columns={columns}
        />
      </>
    );
  };

  if (isExplorePage(pageContext)) {
    return getDefaultExample();
  }

  if (detector?.mode === DetectorModes.BINARY) {
    return getBinaryExample();
  }
  if (detector?.mode === DetectorModes.MULTICHOICE) {
    return getMultichoiceExample();
  }
  if (detector?.mode === DetectorModes.COUNT) {
    return getCountExample();
  }
  if (detector?.mode === DetectorModes.TEXT_RECOGNITION) {
    return getTextExample();
  }
  if (detector?.mode === DetectorModes.BOUNDING_BOX) {
    return getBoundingBoxExample();
  }

  return getDefaultExample();
});
